// src/pages/PersonalPeoplePage.jsx
import React, { useState, useEffect, useCallback } from "react";
import AddWorkerModal from "../components/AddWorkerModal";
import ConfirmModal from "../components/ConfirmModal";
import { PlusIcon, TrashIcon, CheckCircleIcon, ClockIcon } from "@heroicons/react/24/solid";
import { toast } from "react-toastify";
import { SERVER_BASE_URL } from "../config";
import Spinner from "../components/Spinner";

function PersonalPeoplePage() {
  const [showAddWorkerModal, setShowAddWorkerModal] = useState(false);
  const [workers, setWorkers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [defaultWorkers, setDefaultWorkers] = useState({ DOOR: null, MARKETING: null });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [workerToDelete, setWorkerToDelete] = useState(null);
  const [selectedWorkerIds, setSelectedWorkerIds] = useState([]);
  const [showBulkConfirmModal, setShowBulkConfirmModal] = useState(false);
  const [showDeleteInviteConfirm, setShowDeleteInviteConfirm] = useState(false);
  const [inviteToDelete, setInviteToDelete] = useState(null);

  const isMobile = () => window.innerWidth <= 768;

  const fetchWorkers = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await fetch(`${SERVER_BASE_URL}/api/users/my-workers`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) throw new Error(`Error fetching workers: ${response.status}`);
      const data = await response.json();
      setWorkers(data.workers);
      setDefaultWorkers(data.default_workers || { DOOR: null, MARKETING: null });
    } catch (error) {
      toast.error("Failed to load staff list.");
    }
  }, []);

  const fetchInvitations = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const response = await fetch(`${SERVER_BASE_URL}/api/worker_invitations/my-invitations`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) return;
      const data = await response.json();
      if (data.invitations && Array.isArray(data.invitations)) setInvitations(data.invitations);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchWorkers();
      await fetchInvitations();
    };
    loadData();
  }, [fetchWorkers, fetchInvitations]);

  const handleDeleteClick = (worker) => {
    setWorkerToDelete(worker);
    setShowConfirmModal(true);
  };

  const handleDeleteInviteClick = (invite) => {
    setInviteToDelete(invite);
    setShowDeleteInviteConfirm(true);
  };

  const confirmDeleteInvite = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const response = await fetch(`${SERVER_BASE_URL}/api/worker_invitations/${inviteToDelete.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        toast.error("Failed to delete invitation.");
        return;
      }
      toast.success("Invitation deleted successfully!");
      setInvitations((prev) => prev.filter((invite) => invite.id !== inviteToDelete.id));
    } finally {
      setShowDeleteInviteConfirm(false);
      setInviteToDelete(null);
    }
  };

  const confirmDeleteWorker = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${SERVER_BASE_URL}/api/users/${workerToDelete.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        toast.error("Failed to delete worker.");
        return;
      }
      toast.success("Worker deleted successfully!");
      setWorkers((prevWorkers) =>
        prevWorkers.filter((worker) => worker.id !== workerToDelete.id)
      );
      setSelectedWorkerIds((prev) =>
        prev.filter((id) => id !== workerToDelete.id)
      );
    } finally {
      setShowConfirmModal(false);
      setWorkerToDelete(null);
    }
  };

  const cancelDeleteWorker = () => {
    setShowConfirmModal(false);
    setWorkerToDelete(null);
  };
  const cancelDeleteInvite = () => {
    setShowDeleteInviteConfirm(false);
    setInviteToDelete(null);
  };

  const handleDefaultWorkerChange = async (event, workerId, workerRole) => {
    const isChecked = event.target.checked;
    const token = localStorage.getItem("token");
    let payload;
    if (isChecked) {
      payload = { worker_role: workerRole, worker_id: workerId };
      setDefaultWorkers((prev) => ({ ...prev, [workerRole]: workerId }));
    } else {
      payload = { worker_role: workerRole, worker_id: null };
      setDefaultWorkers((prev) => ({ ...prev, [workerRole]: null }));
    }
    try {
      const response = await fetch(`${SERVER_BASE_URL}/api/users/me/default-worker`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) toast.error("Failed to set default staff member.");
      else toast.success("Default staff member updated successfully!");
    } catch {
      toast.error("Error setting default staff member.");
    }
  };

  const handleCheckboxChangeWorker = (workerId) => {
    setSelectedWorkerIds((prev) =>
      prev.includes(workerId)
        ? prev.filter((id) => id !== workerId)
        : [...prev, workerId]
    );
  };

  const handleSelectAllWorkers = () => {
    if (selectedWorkerIds.length === workers.length) {
      setSelectedWorkerIds([]);
    } else {
      setSelectedWorkerIds(workers.map((worker) => worker.id));
    }
  };

  const handleDeleteSelectedWorkers = async () => {
    try {
      const token = localStorage.getItem("token");
      await Promise.all(
        selectedWorkerIds.map((id) =>
          fetch(`${SERVER_BASE_URL}/api/users/${id}`, {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          })
        )
      );
      toast.success("Selected workers deleted successfully!");
      setWorkers((prevWorkers) =>
        prevWorkers.filter((worker) => !selectedWorkerIds.includes(worker.id))
      );
      setSelectedWorkerIds([]);
    } finally {
      setShowBulkConfirmModal(false);
    }
  };

  const handleWorkerRoleChange = async (workerId, newRole) => {
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(`${SERVER_BASE_URL}/api/users/${workerId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ worker_role: newRole }),
      });
      if (!response.ok) throw new Error();
      toast.success("Worker role updated successfully!");
      setWorkers((prevWorkers) =>
        prevWorkers.map((worker) =>
          worker.id === workerId ? { ...worker, worker_role: newRole } : worker
        )
      );
    } catch {
      toast.error("Error updating worker role.");
    }
  };

  const handleInvitationCreated = () => {
    fetchWorkers();
    fetchInvitations();
    setShowAddWorkerModal(false);
  };

  const allStaffMembers = [
    ...workers.map(worker => ({
      ...worker,
      isActive: true,
      type: 'worker'
    })),
    ...invitations.map(invite => ({
      id: invite.id,
      name: invite.invitee_name || invite.invitee_email,
      email: invite.invitee_email,
      worker_role: invite.worker_role,
      isActive: false,
      type: 'invitation',
      originalData: invite
    }))
  ];

  return (
    <div>
      {/* Cards */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold mb-6 text-gray-800">Staff Management</h1>
        <div className="hidden sm:grid sm:grid-cols-3 gap-4 mb-6">
          <div className="p-4 rounded-lg bg-indigo-50">
            <p className="text-sm text-gray-600 mb-1">Total Staff</p>
            <p className="text-2xl font-bold text-gray-800">{workers.length}</p>
          </div>
          <div className="p-4 rounded-lg bg-indigo-50">
            <p className="text-sm text-gray-600 mb-1">Default Door</p>
            <p className="text-2xl font-bold text-gray-800">
              {defaultWorkers.DOOR ? workers.find(w => w.id === defaultWorkers.DOOR)?.name || "Set" : "None"}
            </p>
          </div>
          <div className="p-4 rounded-lg bg-indigo-50">
            <p className="text-sm text-gray-600 mb-1">Default Marketing</p>
            <p className="text-2xl font-bold text-gray-800">
              {defaultWorkers.MARKETING ? workers.find(w => w.id === defaultWorkers.MARKETING)?.name || "Set" : "None"}
            </p>
          </div>
        </div>
        <div className="flex flex-col md:flex-row justify-between space-y-4 md:space-y-0">
          <div className="flex space-x-2">
            <button
              onClick={() => setShowAddWorkerModal(true)}
              className="bg-indigo-600 text-white font-bold px-4 py-2 rounded flex items-center hover:bg-indigo-700 transition duration-200"
            >
              <PlusIcon className="mr-2 h-5 w-5" />
              Add Staff Member
            </button>
            {selectedWorkerIds.length > 0 && (
              <button
                onClick={() => setShowBulkConfirmModal(true)}
                className="flex items-center bg-white text-gray-600 px-4 py-2 rounded hover:bg-gray-50 border border-gray-300 transition duration-200"
              >
                <TrashIcon className="h-5 w-5 mr-2" />
                ({selectedWorkerIds.length})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        {loading ? (
          <div className="py-20 flex justify-center items-center">
            <Spinner />
          </div>
        ) : allStaffMembers.length === 0 ? (
          <div className="py-10 text-center text-gray-500">
            No staff members available. Add your first staff member to get started.
          </div>
        ) : (
          <div>
            {/* Table header */}
            <div className="hidden sm:grid sm:grid-cols-[40px_100px_100px_minmax(100px,1fr)_100px_80px] items-center bg-gray-100 rounded-t-lg px-4 py-2">
              <div className="text-center">
                <input
                  type="checkbox"
                  style={{ marginLeft: "5px" }}
                  checked={selectedWorkerIds.length === workers.length && workers.length > 0}
                  onChange={handleSelectAllWorkers}
                />
              </div>
              <div className="font-semibold text-gray-600 text-center">Default</div>
              <div className="font-semibold text-gray-600 text-center">Role</div>
              <div className="font-semibold text-gray-600 text-center">Name</div>
              <div className="font-semibold text-gray-600 text-center">Status</div>
              <div className="font-semibold text-gray-600 text-center">Actions</div>
            </div>
            {/* Workers and invitations */}
            {allStaffMembers.map((item) => (
              <React.Fragment key={item.id}>
                {item.type === 'worker' ? (
                  // WORKER ROW (email now shown under name)
                  <>
                    <div className="hidden sm:grid sm:grid-cols-[40px_100px_100px_minmax(100px,1fr)_100px_80px] items-center border-b border-indigo-100 p-2 hover:bg-indigo-50 transition duration-200 cursor-default bg-indigo-50">
                      <div className="text-center">
                        <input
                          type="checkbox"
                          className="mx-auto"
                          checked={selectedWorkerIds.includes(item.id)}
                          onChange={() => handleCheckboxChangeWorker(item.id)}
                        />
                      </div>
                      <div className="text-center">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={
                              item.worker_role &&
                              defaultWorkers[item.worker_role.toUpperCase()] === item.id
                            }
                            onChange={(e) =>
                              handleDefaultWorkerChange(
                                e,
                                item.id,
                                (item.worker_role && item.worker_role.toUpperCase()) || "DOOR"
                              )
                            }
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 rounded-full peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-500 dark:bg-gray-700 peer-checked:bg-indigo-500"></div>
                          <span className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transform transition-transform duration-200 ease-in-out peer-checked:translate-x-[1.30rem]"></span>
                        </label>
                      </div>
                      <div className="text-center">
                        <select
                          value={item.worker_role || ""}
                          onChange={(e) => handleWorkerRoleChange(item.id, e.target.value)}
                          className="border rounded py-1 px-2 bg-white text-gray-700 w-full max-w-[90px] mx-auto"
                          style={{ fontSize: isMobile() ? '0.9rem' : '1rem' }}
                        >
                          <option value="">Select</option>
                          <option value="DOOR">DOOR</option>
                          <option value="MARKETING">MARKETING</option>
                        </select>
                      </div>
                      {/* ВАЖНО: ИМЯ + email */}
                      <div className="text-base font-medium text-black text-center overflow-hidden whitespace-nowrap text-ellipsis" style={{ fontSize: isMobile() ? '0.9rem' : '1rem' }}>
                        {item.name}
                        <div className="text-xs text-gray-500 font-normal">{item.email}</div>
                      </div>
                      <div className="flex justify-center items-center">
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full flex items-center">
                          <CheckCircleIcon className="h-3 w-3 mr-1" />
                          Active
                        </span>
                      </div>
                      <div className="flex space-x-2 justify-center px-2">
                        <button
                          onClick={() => handleDeleteClick(item)}
                          className="text-gray-500 hover:text-red-600"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    {/* Mobile row, тоже с email под именем */}
                    <div className="sm:hidden flex justify-between items-start border-b border-indigo-100 p-2 hover:bg-indigo-50 transition duration-200 bg-indigo-50">
                      <div className="flex items-start space-x-2">
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          checked={selectedWorkerIds.includes(item.id)}
                          onChange={() => handleCheckboxChangeWorker(item.id)}
                        />
                        <div>
                          <div className="flex items-center">
                            <div className="text-base font-medium text-black">
                              {item.name}
                            </div>
                            <span className="ml-2 px-1.5 py-0.5 bg-green-100 text-green-800 text-xs rounded-full flex items-center">
                              <CheckCircleIcon className="h-2 w-2 mr-0.5" />
                              Active
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">{item.email}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            Role: {item.worker_role || "Not set"}
                          </div>
                          <div className="flex items-center mt-2 space-x-2">
                            <div className="text-xs text-gray-500">Default:</div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={
                                  item.worker_role &&
                                  defaultWorkers[item.worker_role.toUpperCase()] === item.id
                                }
                                onChange={(e) =>
                                  handleDefaultWorkerChange(
                                    e,
                                    item.id,
                                    (item.worker_role && item.worker_role.toUpperCase()) || "DOOR"
                                  )
                                }
                                className="sr-only peer"
                              />
                              <div className="w-8 h-4 bg-gray-200 rounded-full peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-500 dark:bg-gray-700 peer-checked:bg-indigo-500"></div>
                              <span className="absolute left-1 top-1 w-2 h-2 bg-white rounded-full transform transition-transform duration-200 ease-in-out peer-checked:translate-x-4"></span>
                            </label>
                            <div className="text-xs text-gray-500 ml-2">Role:</div>
                            <select
                              value={item.worker_role || ""}
                              onChange={(e) => handleWorkerRoleChange(item.id, e.target.value)}
                              className="border rounded py-0.5 px-1 bg-white text-gray-700 text-xs"
                            >
                              <option value="">Select</option>
                              <option value="DOOR">DOOR</option>
                              <option value="MARKETING">MARKETING</option>
                            </select>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteClick(item)}
                        className="text-gray-500 hover:text-red-600 mt-1"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </>
                ) : (
                  // INVITATION ROW (тут не менял)
                  <>
                    <div className="hidden sm:grid sm:grid-cols-[40px_100px_100px_minmax(100px,1fr)_100px_80px] items-center border-b border-yellow-100 p-2 hover:bg-yellow-50 transition duration-200 cursor-default bg-yellow-50">
                      <div></div>
                      <div></div>
                      <div className="text-center">{item.worker_role || "Not set"}</div>
                      <div className="text-base font-medium text-black text-center overflow-hidden whitespace-nowrap text-ellipsis">
                        {item.name}
                        <div className="text-xs text-gray-500 font-normal">{item.email}</div>
                      </div>
                      <div className="flex justify-center items-center">
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full flex items-center">
                          <ClockIcon className="h-3 w-3 mr-1" />
                          Pending
                        </span>
                      </div>
                      <div className="flex space-x-2 justify-center px-2">
                        <button
                          onClick={() => handleDeleteInviteClick(item.originalData)}
                          className="text-gray-500 hover:text-red-600"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="sm:hidden flex justify-between items-start border-b border-yellow-100 p-2 hover:bg-yellow-50 transition duration-200 bg-yellow-50">
                      <div className="flex items-start space-x-2">
                        <div className="w-4"></div>
                        <div>
                          <div className="flex items-center">
                            <div className="text-base font-medium text-black">
                              {item.name}
                            </div>
                            <span className="ml-2 px-1.5 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full flex items-center">
                              <ClockIcon className="h-2 w-2 mr-0.5" />
                              Pending
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">{item.email}</div>
                          {item.worker_role && (
                            <div className="text-xs text-gray-500 mt-1">
                              Role: {item.worker_role}
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteInviteClick(item.originalData)}
                        className="text-gray-500 hover:text-red-600 mt-1"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
      {/* Empty state */}
      {!loading && allStaffMembers.length === 0 && (
        <div className="bg-white rounded-lg shadow-md p-8 sm:p-12 text-center mb-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No staff members found</h3>
          <p className="text-gray-500 mb-6">Add your first staff member to get started.</p>
          <button
            onClick={() => setShowAddWorkerModal(true)}
            className="bg-indigo-600 text-white font-bold px-4 py-2 rounded hover:bg-indigo-700 transition duration-200"
          >
            <PlusIcon className="inline-block h-5 w-5 mr-2" />
            Add Staff Member
          </button>
        </div>
      )}

      {/* Modals */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={cancelDeleteWorker}
        onConfirm={confirmDeleteWorker}
        message="Are you sure you want to delete this worker? This will permanently remove the worker's account."
      />
      <ConfirmModal
        isOpen={showDeleteInviteConfirm}
        onClose={cancelDeleteInvite}
        onConfirm={confirmDeleteInvite}
        message="Are you sure you want to cancel this invitation? The invited person will no longer be able to join."
      />
      <ConfirmModal
        isOpen={showBulkConfirmModal}
        onClose={() => setShowBulkConfirmModal(false)}
        onConfirm={handleDeleteSelectedWorkers}
        message="Are you sure you want to delete the selected workers? This will permanently remove their accounts."
      />
      <AddWorkerModal
        isOpen={showAddWorkerModal}
        onClose={() => setShowAddWorkerModal(false)}
        token={localStorage.getItem("token") || ""}
        onCreated={handleInvitationCreated}
      />
    </div>
  );
}

export default PersonalPeoplePage;
